"use client";

import { quizLinkLabel } from "@/lib/i18n/quiz-link-language";

import type { Locale } from "@/lib/i18n/locales";
import { quizProgressCopy } from "@/lib/i18n/quiz-progress-copy";

import type { Route } from "next";
import Link from "next/link";
import { useEffect, useState } from "react";

import type { QuizId } from "./registry";
import {
  BUS_QUIZ_CLEAR_EVENT,
  quizStorageKeys,
  quizProgressLocaleForHref,
  BUS_QUIZ_COMPLETION_PREFIX,
  BUS_QUIZ_PROGRESS_PREFIX,
  BUS_QUIZ_STORAGE_VERSION,
} from "./bus-quiz-storage";

type StoredProgress = {
  version: number;
  currentIndex: number;
  answers: Record<number, number>;
  checkedIds: number[];
};

type BusQuizCtaProps = {
  locale?: Locale;
  persistenceRevision?: string;
  quizId: QuizId;
  href: Route;
  className?: string;
  defaultLabel?: string;
  continueLabel?: string;
  prefetch?: boolean;
};

const hasMeaningfulProgress = (raw: string | null) => {
  if (!raw) return false;
  try {
    const parsed = JSON.parse(raw) as StoredProgress | null;
    if (!parsed || parsed.version !== BUS_QUIZ_STORAGE_VERSION) return false;
    const hasIndex = typeof parsed.currentIndex === "number" && parsed.currentIndex > 0;
    const hasAnswers = Object.keys(parsed.answers ?? {}).length > 0;
    const hasChecked = Array.isArray(parsed.checkedIds) && parsed.checkedIds.length > 0;
    return hasIndex || hasAnswers || hasChecked;
  } catch (error) {
    console.warn("Failed to parse saved quiz progress", error);
    return false;
  }
};

export default function BusQuizCta({
  quizId,
  locale = "en",
  persistenceRevision,
  href,
  className,
  defaultLabel: suppliedDefaultLabel,
  continueLabel: suppliedContinueLabel,
  prefetch = false,
}: BusQuizCtaProps) {
  const copy = quizProgressCopy(locale);
  const defaultLabel = quizLinkLabel(suppliedDefaultLabel ?? copy.start, locale, href);
  const continueLabel = quizLinkLabel(suppliedContinueLabel ?? copy.continue, locale, href);
  const [label, setLabel] = useState(defaultLabel);

  useEffect(() => {
    const progressKey = quizStorageKeys(quizId, quizProgressLocaleForHref(href), persistenceRevision).progress;
    const completionKey = quizStorageKeys(quizId, quizProgressLocaleForHref(href), persistenceRevision).completion;

    const updateLabel = () => {
      try {
        if (window.localStorage.getItem(completionKey)) {
          setLabel(defaultLabel);
          return;
        }
        const raw = window.localStorage.getItem(progressKey);
        setLabel(hasMeaningfulProgress(raw) ? continueLabel : defaultLabel);
      } catch (error) {
        console.warn("Failed to read quiz progress", error);
        setLabel(defaultLabel);
      }
    };

    const handleStorage = (event: StorageEvent) => {
      if (!event.key) return;
      if (
        event.key.startsWith(BUS_QUIZ_PROGRESS_PREFIX) ||
        event.key.startsWith(BUS_QUIZ_COMPLETION_PREFIX)
      ) {
        updateLabel();
      }
    };

    updateLabel();
    window.addEventListener("storage", handleStorage);
    window.addEventListener(BUS_QUIZ_CLEAR_EVENT, updateLabel);
    return () => {
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener(BUS_QUIZ_CLEAR_EVENT, updateLabel);
    };
  }, [continueLabel, defaultLabel, quizId, href, persistenceRevision]);

  return (
    <Link<string> href={href} prefetch={prefetch} className={className}>
      {label}
    </Link>
  );
}
